<template>
  <div class="video-embed">
    <!-- Loading state (embed loading) -->
    <div v-if="embedLoader.isLoading.value" class="video-embed__loading" />

    <!-- The load failed: what it means to a visitor, never the API's sentence -->
    <PlayerRefusal
      v-else-if="embedLoader.failure.value"
      :failure="embedLoader.failure.value"
      :can-retry="!!props.embedId"
      @retry="handleRetry"
    />

    <!-- Playback info loading (initial load only — during playlist navigation the player stays alive) -->
    <div v-else-if="isLoadingPlaybackInfo && !currentPlaybackInfo" class="video-embed__loading" />

    <!-- Billing gate: owner out of credits — neutral "unavailable" state -->
    <PlayerUnavailable v-else-if="isBlocked" media="video" />

    <!-- Player -->
    <VideoPlayer
      v-else-if="playerConfig && currentPlaybackInfo"
      ref="videoPlayerRef"
      :config="playerConfig"
      :playback-info="currentPlaybackInfo"
      :poster="posterUrl"
      :theme="theme"
      :player-theme="resolvedPlayerTheme"
      :show-controls="controls"
      :pin-controls="pinControls"
      :show-playlist="showPlaylist"
      :collection-display-mode="collectionDisplayMode"
      :overlays="currentOverlays"
      :show-logo="showLogo"
      :logo-url="logoUrl"
      :logo-position="logoPosition"
      :logo-click-url="logoClickUrl"
      :seeking="seeking"
      @item-change="handleItemChange"
      @play="$emit('play')"
      @pause="$emit('pause')"
      @ended="$emit('ended')"
      @time-update="$emit('timeUpdate', $event)"
      @duration-change="$emit('durationChange', $event)"
      @volume-change="(volume, muted) => $emit('volumeChange', volume, muted)"
      @quality-change="$emit('qualityChange', $event)"
      @fullscreen-change="$emit('fullscreenChange', $event)"
      @error="$emit('error', $event)"
      @overlay-submit="handleOverlaySubmit"
    />
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, watch, toRef } from 'vue'
  import type { PropType } from 'vue'
  import type { Embed, PlayerTheme } from '../../types/embed'
  import type { VideoAssetListItem, VideoPlaybackInfo } from '../../types/asset'
  import type { VideoPlayerConfig, CollectionDisplayMode } from '../types'
  import type { PlayerError } from '../../types/base'
  import type { CollectionItem } from '../../types/player'
  import { submitRating, type SubmitRatingRequest } from '@/api/visitor/ratings'
  import { useEmbedLoader } from '../../common/composables/useEmbedLoader'
  import PlayerRefusal from '../../common/components/PlayerRefusal.vue'
  import { usePlaybackInfoCache } from '../../common/composables/usePlaybackInfoCache'
  import { createUUID } from '../../types/base'
  import VideoPlayer from './VideoPlayer.vue'
  import PlayerUnavailable from '../../common/components/PlayerUnavailable.vue'

  const props = defineProps({
    /** Embed ID to load */
    embedId: {
      type: String,
      default: undefined,
    },
    /** Embed data (if already loaded) */
    embed: {
      type: Object as PropType<Embed<VideoAssetListItem>>,
      default: undefined,
    },
    /** Viewer ID for analytics */
    viewerId: {
      type: String,
      default: undefined,
    },
    /**
     * Index of the item to open on.
     *
     * The collection shell owns the current index (a mixed collection can hand
     * the next item to another player), so it seeds the video player here and
     * follows it back through `itemChange`.
     */
    currentIndex: {
      type: Number,
      default: 0,
    },
    /** Auto-play when loaded */
    autoPlay: {
      type: Boolean,
      default: false,
    },
    /** Loop playback */
    loop: {
      type: Boolean,
      default: false,
    },
    /** Auto-play next item in a collection when the current one ends */
    autoPlayNext: {
      type: Boolean,
      default: true,
    },
    /** Play ads configured on the embed (pre-roll/mid-roll/post-roll) */
    showAds: {
      type: Boolean,
      default: true,
    },
    /** Start muted */
    muted: {
      type: Boolean,
      default: false,
    },
    /** Show player controls */
    controls: {
      type: Boolean,
      default: true,
    },
    /** Keep the control bar always visible (disables auto-hide) */
    pinControls: {
      type: Boolean,
      default: false,
    },
    /** Show playlist for collections */
    showPlaylist: {
      type: Boolean,
      default: true,
    },
    /** Auto quality selection */
    autoQuality: {
      type: Boolean,
      default: true,
    },
    /**
     * Offer the source download — the in-app player's switch. An embed's own `playerConfig`
     * answers first, because that is the download route's gate.
     */
    enableDownload: {
      type: Boolean,
      default: false,
    },
    /** Show chapters */
    showChapters: {
      type: Boolean,
      default: true,
    },
    /** Show eco mode button */
    showEcoMode: {
      type: Boolean,
      default: true,
    },
    /**
     * Start in eco mode, when the caller knows better than the embed response.
     *
     * The value normally travels through `EmbedOut.player_config` (resolved
     * server-side from the preset), which means a settings screen cannot show
     * the effect of the toggle it just flipped. `undefined` keeps the embed's
     * own value, so nothing changes for the public player.
     */
    ecoModeDefault: {
      type: Boolean,
      default: undefined,
    },
    /**
     * Start the default subtitle track, when the page that mounts the player decides it — a
     * channel's, whose mount options carry it with no embed response behind them. `undefined`
     * keeps the embed's own value.
     */
    subtitlesAutoStart: {
      type: Boolean,
      default: undefined,
    },
    /** Show info button */
    showInfoButton: {
      type: Boolean,
      default: true,
    },
    /** Enable picture-in-picture */
    enablePip: {
      type: Boolean,
      default: true,
    },
    /** Enable fullscreen */
    enableFullscreen: {
      type: Boolean,
      default: true,
    },
    /** Show title/duration on homepage overlay */
    showHomepageInfo: {
      type: Boolean,
      default: true,
    },
    /** Show play button on homepage overlay */
    showHomepagePlayButton: {
      type: Boolean,
      default: true,
    },
    /** Player theme */
    theme: {
      type: String as PropType<'dark' | 'light'>,
      default: 'dark',
    },
    /** Prefetch adjacent videos */
    prefetchAdjacent: {
      type: Boolean,
      default: true,
    },
    /** Collection display mode */
    collectionDisplayMode: {
      type: String as PropType<CollectionDisplayMode>,
      default: 'drawer',
    },
    /** How the poster thumbnail fits the player area */
    posterFit: {
      type: String as PropType<'contain' | 'cover'>,
      default: 'contain',
    },
    /** Start playback at this time in seconds */
    startAt: {
      type: Number,
      default: undefined,
    },
    /** Master switch for the branding logo */
    showLogo: {
      type: Boolean,
      default: true,
    },
    /** Logo URL override */
    logoUrl: {
      type: String,
      default: undefined,
    },
    /** Logo position override */
    logoPosition: {
      type: String as PropType<'top_left' | 'top_right' | 'bottom_left' | 'bottom_right' | 'control_bar'>,
      default: undefined,
    },
    /** Logo click URL override */
    logoClickUrl: {
      type: String,
      default: undefined,
    },
    /** Primary color override */
    primaryColor: {
      type: String,
      default: undefined,
    },
    /** Custom CSS override to inject into the player */
    customCss: {
      type: String,
      default: undefined,
    },
    /** Whether the viewer may move the playhead (`seeking`) */
    seeking: {
      type: Boolean,
      default: true,
    },
  })

  const emit = defineEmits<{
    embedLoad: [embed: Embed<VideoAssetListItem>]
    itemChange: [index: number, item: CollectionItem<VideoAssetListItem>]
    play: []
    pause: []
    ended: []
    timeUpdate: [currentTime: number]
    durationChange: [duration: number]
    volumeChange: [volume: number, muted: boolean]
    qualityChange: [quality: string | null]
    fullscreenChange: [isFullscreen: boolean]
    error: [error: PlayerError]
  }>()

  // Embed loader
  const embedIdRef = toRef(props, 'embedId')
  const embedPropRef = toRef(props, 'embed')

  const embedLoader = useEmbedLoader<VideoAssetListItem>({
    embedId: embedIdRef,
    embed: embedPropRef,
    autoLoad: true,
  })

  // Playback info cache
  const playbackInfoCache = usePlaybackInfoCache<VideoPlaybackInfo>({
    playbackToken: computed(() => embedLoader.embed.value?.playbackToken),
    itemToken: computed(() => embedLoader.embed.value?.itemToken),
    collectionPlaybackId: computed(() => embedLoader.embed.value?.collectionPlaybackId),
    sessionId: computed(() => embedLoader.embed.value?.sessionId),
  })

  // Template ref for VideoPlayer
  const videoPlayerRef = ref<InstanceType<typeof VideoPlayer> | null>(null)

  // Current item index — seeded by the shell, then owned by the player while it
  // stays mounted (its own next/prev/playlist drive it, and report back up).
  const currentIndex = ref(props.currentIndex)
  const isLoadingPlaybackInfo = ref(false)

  // Current playback info — keeps the last known value while loading the next item
  // so the VideoPlayer is never destroyed/recreated during playlist navigation.
  const _livePlaybackInfo = computed<VideoPlaybackInfo | undefined>(() => {
    const embed = embedLoader.embed.value
    if (!embed || embed.items.length === 0) return undefined

    const currentItem = embed.items[currentIndex.value]
    if (!currentItem) return undefined

    return playbackInfoCache.get(currentItem.asset.uid)
  })

  const _lastPlaybackInfo = ref<VideoPlaybackInfo | undefined>()

  watch(
    _livePlaybackInfo,
    (val) => {
      if (val) _lastPlaybackInfo.value = val
    },
    { immediate: true },
  )

  const currentPlaybackInfo = computed(() => _livePlaybackInfo.value ?? _lastPlaybackInfo.value)

  // Public billing gate: the backend withholds playback (no media URL) when the
  // owner's wallet is empty; show a neutral "unavailable" state instead.
  const isBlocked = computed(() => currentPlaybackInfo.value?.blocked === true)

  // Resolved player theme from embed data, with overrides from props
  const resolvedPlayerTheme = computed<PlayerTheme | undefined>(() => {
    const base = embedLoader.embed.value?.playerTheme
    const overrides: Partial<PlayerTheme> = {}
    if (props.theme) overrides.baseTheme = props.theme as 'dark' | 'light'
    if (props.primaryColor) overrides.primaryColor = props.primaryColor
    if (props.customCss) overrides.customCss = props.customCss
    if (Object.keys(overrides).length === 0) return base
    return { ...base, ...overrides }
  })

  // Poster URL
  const posterUrl = computed(() => {
    const embed = embedLoader.embed.value
    if (!embed || embed.items.length === 0) return undefined
    return embed.items[currentIndex.value]?.asset.thumbnailUrl
  })

  // Player config derived from embed
  const playerConfig = computed<VideoPlayerConfig | null>(() => {
    const embed = embedLoader.embed.value
    if (!embed) return null

    return {
      uid: embed.uid,
      title: embed.name,
      items: embed.items.map((item, index) => ({
        asset: item.asset,
        position: index,
        customTitle: item.customTitle,
        customDescription: item.customDescription,
      })),
      currentIndex: currentIndex.value,
      viewerId: createUUID(props.viewerId || 'viewer'),
      autoPlay: props.autoPlay,
      autoPlayNext: props.autoPlayNext,
      loop: props.loop,
      muted: props.muted,
      autoQuality: props.autoQuality,
      showChapters: props.showChapters,
      showEcoMode: props.showEcoMode,
      showInfoButton: props.showInfoButton,
      pip: props.enablePip,
      enableFullscreen: props.enableFullscreen,
      showHomepageInfo: props.showHomepageInfo,
      showHomepagePlayButton: props.showHomepagePlayButton,
      posterFit: props.posterFit,
      startAt: props.startAt,
      allowSourceDownload: embed?.playerConfig?.allowSourceDownload ?? props.enableDownload,
      sourceDownload: embed?.sourceDownload,
      ecoModeDefault: props.ecoModeDefault ?? embed?.playerConfig?.ecoModeDefault ?? false,
      // The token that reaches the item playing: an in-app item's own, else the publication's.
      playbackToken: playbackInfoCache.tokenOf(embed.items[currentIndex.value]?.asset.uid),
      adsConfig: props.showAds && embed?.adsConfig
        ? {
            preRoll: [
              {
                id: 'pre-roll-0',
                type: 'pre-roll' as const,
                vastUrl: embed.adsConfig.vastUrl,
                forceSkipAfter: embed.adsConfig.forceSkipAfter,
              },
            ],
          }
        : undefined,
      preferredSubtitleLanguage:
        embed?.playerConfig?.subtitlesDefaultMode === 'auto_detect' ? navigator.language.split('-')[0] : undefined,
      subtitlesAutoStart: props.subtitlesAutoStart ?? embed?.playerConfig?.subtitlesAutoStart ?? false,
    }
  })

  // The shell may move the playlist on its own (playlist strip/drawer shown
  // around a third-party item); mirror it into the running player.
  watch(
    () => props.currentIndex,
    (index) => {
      if (index !== currentIndex.value) videoPlayerRef.value?.goToIndex(index)
    },
  )

  /**
   * Handle item change from VideoPlayer
   */
  function handleItemChange(index: number, item: CollectionItem<VideoAssetListItem>): void {
    currentIndex.value = index
    emit('itemChange', index, item)

    // Only video items have video playback info; a mixed collection hands the
    // other types to their own player, and the shell unmounts this one.
    if (item.asset.type !== 'video') return

    // Load playback info for the new current item if not cached
    if (!playbackInfoCache.has(item.asset.uid)) {
      isLoadingPlaybackInfo.value = true
      loadPlaybackInfo(item.asset.uid)
        .catch(() => {})
        .finally(() => {
          isLoadingPlaybackInfo.value = false
        })
    }

    // Prefetch adjacent
    if (props.prefetchAdjacent && embedLoader.embed.value) {
      const items = embedLoader.embed.value.items
      const adjacentUids: string[] = []
      // Prefetch video neighbours only — the other types are played by another
      // component, which opens its own playback session when it mounts.
      if (index > 0 && items[index - 1].asset.type === 'video') adjacentUids.push(items[index - 1].asset.uid)
      if (index < items.length - 1 && items[index + 1].asset.type === 'video')
        adjacentUids.push(items[index + 1].asset.uid)
      if (adjacentUids.length > 0) {
        playbackInfoCache.fetchBatch(adjacentUids).catch(() => {})
      }
    }
  }

  /**
   * Load playback info for an asset
   */
  async function loadPlaybackInfo(assetUid: string): Promise<VideoPlaybackInfo> {
    return playbackInfoCache.fetchOne(assetUid)
  }

  /**
   * Handle overlay submit — routes to the appropriate handler based on action type
   */
  async function handleOverlaySubmit(uid: string, data: unknown): Promise<void> {
    console.log('[VideoEmbed] handleOverlaySubmit:', uid, data)
    // Recommendations overlay actions
    if (data && typeof data === 'object' && 'action' in data) {
      const action = data as { action: string; assetUid?: string }

      if (action.action === 'replay') {
        videoPlayerRef.value?.dismissAllOverlays()
        videoPlayerRef.value?.seek(0)
        videoPlayerRef.value?.play()
        return
      }

      if (action.action === 'play' && action.assetUid) {
        videoPlayerRef.value?.dismissAllOverlays()
        await switchToRecommendedVideo(action.assetUid)
        return
      }
    }

    // Rating overlay submission
    const ratingData = data as { rating: number; comment: string }
    const token = embedLoader.embed.value?.playbackToken
    const sessionId = currentPlaybackInfo.value?.sessionId
    const currentItem = embedLoader.embed.value?.items[currentIndex.value]

    console.log('[VideoEmbed] handleOverlaySubmit called', {
      uid,
      data,
      token: !!token,
      sessionId,
      assetId: currentItem?.asset.uid,
    })

    if (!token || !sessionId || !currentItem) {
      console.warn('[VideoEmbed] Missing data for rating submission', {
        token: !!token,
        sessionId,
        currentItem: !!currentItem,
      })
      return
    }

    const payload: SubmitRatingRequest = {
      playback_token: token,
      asset_id: currentItem.asset.uid,
      session_id: sessionId,
      overlay_id: uid,
      rating: ratingData.rating,
      message: ratingData.comment || '',
    }
    try {
      await submitRating(payload)
    } catch (err) {
      console.error('[VideoEmbed] Failed to submit rating:', err)
    }
  }

  /**
   * Switch to a recommended video by asset UID.
   * Builds a synthetic embed item from the recommendation payload and navigates to it.
   */
  async function switchToRecommendedVideo(assetUid: string): Promise<void> {
    const embed = embedLoader.embed.value
    if (!embed) return

    // The strip is the playing entry's: only a single video's one entry carries one
    const recsOverlay = embed.items[currentIndex.value]?.overlays?.find((o) => o.overlayType === 'recommendations')
    const rawItems = recsOverlay?.payload?.items
    if (!Array.isArray(rawItems) || rawItems.length === 0) return

    const recItem = rawItems.find((item: any) => item.asset_uid === assetUid)
    if (!recItem) return

    // Load playback info first
    let playbackInfo: VideoPlaybackInfo
    try {
      playbackInfo = await loadPlaybackInfo(recItem.asset_uid)
    } catch (err) {
      console.error('[VideoEmbed] Failed to load playback info for recommendation:', err)
      return
    }

    // Build a synthetic item and inject it into the player's internal items list
    const syntheticAsset = {
      uid: recItem.asset_uid,
      name: recItem.name,
      type: 'video',
      thumbnailUrl: recItem.thumbnail_url,
      duration: recItem.duration ?? 0,
      status: 'ready',
    } as VideoAssetListItem

    const player = videoPlayerRef.value?.player
    if (!player) return

    // Replace the single item (don't append — avoids triggering playlist mode). The synthetic entry
    // carries no cards — the recommended video's own are not loaded here — so the strip and the
    // cards of the entry it replaces go with it.
    player.items.value = [{
      asset: syntheticAsset,
      position: 0,
    }]
    embed.items = [{
      asset: syntheticAsset,
      position: 0,
    }]
    currentIndex.value = 0

    // Reset play state before switching (prevents video_end overlays from re-triggering)
    player.playState.value = 'loading'

    // Set playback info so the HLS URL watcher triggers the source switch
    player.setPlaybackInfo(recItem.asset_uid, playbackInfo)

    // Navigate and start
    player.goToIndex(0)
    player.startPlayback()
  }

  /**
   * Handle retry button click
   */
  function handleRetry(): void {
    if (props.embedId) {
      embedLoader.load(props.embedId)
    }
  }

  /**
   * The cards of what is playing: the current entry's own, a single asset's one entry as much as
   * each entry of a playlist — the first one's cards are not the second's.
   */
  const currentOverlays = computed(() => embedLoader.embed.value?.items[currentIndex.value]?.overlays ?? [])

  // Watch for embed load and prefetch first item
  watch(
    () => embedLoader.embed.value,
    async (embed) => {
      if (embed) {
        emit('embedLoad', embed)

        // Load the current item's playback info
        const startItem = embed.items[currentIndex.value]
        if (startItem) {
          isLoadingPlaybackInfo.value = true
          try {
            await loadPlaybackInfo(startItem.asset.uid)
          } catch (err) {
            // Failed to load playback info
          } finally {
            isLoadingPlaybackInfo.value = false
          }
        }
      }
    },
    { immediate: true },
  )

  // Expose methods for external control
  defineExpose({
    embedLoader,
    playbackInfoCache,
    currentIndex,
    reload: () => props.embedId && embedLoader.load(props.embedId),
    goToIndex: (index: number) => videoPlayerRef.value?.goToIndex(index),
    play: () => videoPlayerRef.value?.play(),
    pause: () => videoPlayerRef.value?.pause(),
    seek: (time: number) => videoPlayerRef.value?.seek(time),
    startPlayback: () => videoPlayerRef.value?.startPlayback(),
    duration: computed(() => videoPlayerRef.value?.duration),
  })
</script>

<style lang="scss" scoped>
  .video-embed {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    &__loading {
      flex: 1;
      background: #000;
    }
  }
</style>
